import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import dfAddDays from "date-fns/addDays";
import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";

import LostDelivery from "@/Models/LostDelivery";
import { formatNextAutoReturnAt } from "../DelayedDeliveries";
import api from "../Api";

export const formatNextAutoDisposalAt = formatNextAutoReturnAt;

export const QUERY_SORT = {
  LOST_COMPLETED_AT: "lostCompletedAt",
  COMMENT_CREATED_AT: "commentCreatedAt",
  UNCONFIRMED_COMMENT: "unconfirmedComment",
  DISPOSAL_APPROVED_AT: "disposeConfirmedAt",
  RETURN_APPROVED_AT: "returnApprovedAt",
};

export const REVIEW_FILTER = {
  PENDING: "pending",
  DISPOSAL_APPROVED: "disposalApproved",
  RETURN_APPROVED: "returnApproved",
};

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
};

const zonedNow = getDateByTimeZone();
const initDateFrom = formatDate(dfSubDays(zonedNow, 1));
const initDateTo = formatDate(dfAddDays(zonedNow, 1));

const INITIAL_QUERY = {
  lostCompletedAtFrom: initDateFrom,
  lostCompletedAtTo: initDateTo,
  bookId: "",
  receiverName: "",
  receiverMobile: "",
  review: undefined,
  page: 1,
  pageSize: 20,
  sort: QUERY_SORT.LOST_COMPLETED_AT,
};

const INITIAL_STATE = {
  rows: [],
  totalCount: 0,
  pageCount: 0,
  query: INITIAL_QUERY,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        rows: action.rows,
        totalCount: action.totalCount,
        pageCount: action.pageCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return INITIAL_STATE;
  }
};

const compactQuery = (query = {}) => {
  const next = {};

  Object.keys(query).forEach((key) => {
    const value = query[key];

    if (value === undefined || value === null || value === "") {
      return;
    }

    next[key] = value;
  });

  return next;
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useLostDeliveriesStore = () => {
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(
    async (queryOverride) => {
      const query = compactQuery(queryOverride || state.query);
      const { rows, totalCount } = await api.get("/lost-deliveries", query);
      const items = (rows || []).map((d) => new LostDelivery(d));

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        rows: items,
        totalCount: totalCount || 0,
        pageCount:
          Math.floor((totalCount || 0) / (query.pageSize || 20)) +
          ((totalCount || 0) % (query.pageSize || 20) ? 1 : 0),
      });
    },
    [dispatch, state.query],
  );

  const returnApprove = useCallback(async (bookId) => {
    return await api.post(
      `/lost-deliveries/${encodeURIComponent(bookId)}/return-approve`,
    );
  }, []);

  const disposalApprove = useCallback(async (bookId) => {
    return await api.post(
      `/lost-deliveries/${encodeURIComponent(bookId)}/dispose-approve`,
    );
  }, []);

  const disposalWait = useCallback(async (bookId) => {
    return await api.post(
      `/lost-deliveries/${encodeURIComponent(bookId)}/dispose-wait`,
    );
  }, []);

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({ type: CONSTANTS.SET_QUERY, query });
    },
    [dispatch],
  );

  return {
    state,
    fetchAll,
    returnApprove,
    disposalApprove,
    disposalWait,
    setQuery,
  };
};
